(() => {
  const reduced = () => document.body?.dataset.acMotion === 'reduced'
    || matchMedia('(prefers-reduced-motion: reduce)').matches;
  let loading;

  const ensureSwiper = ({ script, stylesheet }) => {
    if (loading) return loading;
    loading = Promise.all([
      window.Swiper ? Promise.resolve() : loadAsset('script', script),
      loadAsset('link', stylesheet),
    ]).then(() => {
      if (!window.Swiper) throw new Error('Gallery unavailable');
    }).catch((error) => { loading = null; throw error; });
    return loading;
  };

  const loadAsset = (tag, url) => new Promise((resolve, reject) => {
    if (!url) { reject(new Error('Missing gallery resource')); return; }
    const property = tag === 'script' ? 'src' : 'href';
    const target = new URL(url, location.href);
    // Homepage's synchronous bundle already contains these exact rules.
    // Reuse it when a drawer requests Swiper rather than downloading CSS again.
    if (tag === 'link' && target.pathname.endsWith('/ac-product-swiper.min.css')
      && document.body.dataset.acHomeStylesBundled === 'swiper'
      && document.querySelector('link[href*="/ac-home-critical.css"]')?.sheet) {
      resolve(); return;
    }
    let element = [...document.querySelectorAll(`${tag}[${property}]`)]
      .find((item) => new URL(item[property]).pathname === target.pathname);
    if (tag === 'link' && element?.sheet) { resolve(); return; }
    const created = !element;
    element ||= document.createElement(tag);
    const cleanup = () => {
      clearTimeout(timeout);
      element.removeEventListener('load', done);
      element.removeEventListener('error', fail);
    };
    const done = () => { cleanup(); resolve(); };
    const fail = () => {
      cleanup();
      if (created) element.remove();
      reject(new Error('Gallery resource unavailable'));
    };
    const timeout = setTimeout(fail, 12000);
    element.addEventListener('load', done, { once: true });
    element.addEventListener('error', fail, { once: true });
    if (created) {
      if (tag === 'link') element.rel = 'stylesheet';
      else element.async = true;
      element[property] = target.href;
      document.head.append(element);
    }
  });

  const create = ({ scope, stage, track, slides, thumbnails = [], previous, next, current,
    mediaId = (slide) => slide.dataset.mediaId, initialMediaId = '', onChange = () => {}, messages = {} }) => {
    if (!stage || !slides.length) return null;
    const abort = new AbortController();
    const { signal } = abort;
    let swiper;
    let index = -1;
    const initialIndex = Math.max(0, slides.findIndex((slide) => String(mediaId(slide)) === String(initialMediaId)));
    const motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    let lastReduced = reduced();
    const render = (value, emit = true) => {
      const oldIndex = index;
      index = Math.min(slides.length - 1, Math.max(0, Number(value) || 0));
      if (index !== oldIndex) {
        const thumbnail = thumbnails[index];
        const parent = thumbnail?.parentElement;
        let scrollTarget = null;
        // Read before writing, relative to the strip rather than the page's side gutter.
        if (parent) {
          const rect = thumbnail.getBoundingClientRect();
          const left = rect.left - parent.getBoundingClientRect().left + parent.scrollLeft;
          const right = left + rect.width;
          scrollTarget = left < parent.scrollLeft ? left
            : right > parent.scrollLeft + parent.clientWidth ? right - parent.clientWidth : null;
        }
        // Initialize once; later transitions only change the previous and current items.
        for (const i of oldIndex < 0 ? slides.keys() : [oldIndex, index]) {
          slides[i].setAttribute('aria-hidden', String(i !== index));
          slides[i].inert = i !== index;
          if (thumbnails[i]) {
            thumbnails[i].classList.toggle('is-active', i === index);
            thumbnails[i].setAttribute('aria-pressed', String(i === index));
          }
        }
        if (current) {
          current.textContent = String(index + 1);
          // Media controls and failure actions own the space while a video is selected.
          const pagination = current.closest('[data-ac-ref-product-pagination]');
          if (pagination) pagination.hidden = slides[index].dataset.acRefProductMediaType !== 'image';
        }
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
        if (scrollTarget !== null) parent.scrollTo({ left: scrollTarget, behavior: reduced() ? 'auto' : 'smooth' });
      }
      onChange(index, { emit });
    };
    const select = (value, { speed = reduced() ? 0 : 360, focusThumbnail = false } = {}) => {
      const target = Math.min(slides.length - 1, Math.max(0, Number(value) || 0));
      if (swiper) {
        const unchanged = swiper.activeIndex === target;
        swiper.slideTo(target, speed);
        if (unchanged) render(target);
      } else {
        stage.scrollTo({ left: target * stage.clientWidth, behavior: speed ? 'smooth' : 'instant' });
        render(target);
      }
      if (focusThumbnail) thumbnails[target]?.focus({ preventScroll: true });
    };
    const selectMedia = (id, options) => {
      const target = slides.findIndex((slide) => String(mediaId(slide)) === String(id));
      if (target >= 0) select(target, options);
    };
    const strip = thumbnails[0]?.parentElement;
    if (strip) {
      let drag;
      let suppressClick = false;
      strip.addEventListener('pointerdown', (event) => {
        suppressClick = false;
        if (event.pointerType !== 'mouse' || event.button !== 0 || strip.scrollWidth <= strip.clientWidth) return;
        drag = { id: event.pointerId, x: event.clientX, left: strip.scrollLeft, moved: false };
      }, { signal });
      strip.addEventListener('pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          strip.classList.add('is-dragging');
          strip.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        strip.scrollLeft = drag.left - delta;
      }, { signal });
      const endDrag = (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        suppressClick = drag.moved && event.type === 'pointerup';
        drag = null;
        strip.classList.remove('is-dragging');
        if (strip.hasPointerCapture(event.pointerId)) strip.releasePointerCapture(event.pointerId);
      };
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) strip.addEventListener(type, endDrag, { signal });
      strip.addEventListener('pointerleave', (event) => {
        if (drag && !drag.moved) endDrag(event);
      }, { signal });
      strip.addEventListener('click', (event) => {
        if (!suppressClick || event.detail === 0) return;
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { signal, capture: true });
      strip.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    }
    for (const [i, thumbnail] of thumbnails.entries()) {
      thumbnail.addEventListener('click', () => select(i), { signal });
    }
    previous?.addEventListener('click', () => select(index - 1), { signal });
    next?.addEventListener('click', () => select(index + 1), { signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    // Swiper owns gesture detection; a drag must not become an image-modal click,
    // including reduced-motion transitions that finish immediately.
    stage.addEventListener('click', (event) => {
      if (swiper && !swiper.allowClick) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { signal, capture: true });
    scope.addEventListener('keydown', (event) => {
      const withinGallery = stage.contains(event.target) || thumbnails.some((item) => item.contains(event.target))
        || event.target === previous || event.target === next;
      if (event.defaultPrevented || !withinGallery
        || event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft') select(index - 1);
      else if (event.key === 'ArrowRight') select(index + 1);
      else if (event.key === 'Home') select(0);
      else if (event.key === 'End') select(slides.length - 1);
      else return;
      event.preventDefault();
      event.stopPropagation();
    }, { signal });
    if (slides.length > 1 && window.Swiper) {
      swiper = new window.Swiper(stage, {
        a11y: { enabled: true, ...messages, slideRole: track.matches('ul, ol, [role="list"]') ? 'listitem' : 'group' },
        slidesPerView: 1, speed: reduced() ? 0 : 360,
        initialSlide: initialIndex, runCallbacksOnInit: false,
        freeMode: false, loop: false, grabCursor: true,
        lazyPreloadPrevNext: 1,
        keyboard: { enabled: false }, threshold: 6,
        longSwipesMs: 300, longSwipesRatio: 0.2, resistanceRatio: 0.65,
        touchStartPreventDefault: false, watchOverflow: true,
        noSwipingSelector: 'button:not([data-ac-product-modal-opener]), input, select, textarea, video, iframe, model-viewer, [role="button"]',
        focusableElements: 'input, select, option, textarea, button:not([data-ac-product-modal-opener]), video, label',
        preventClicks: true, preventClicksPropagation: true,
        on: { init: (instance) => render(instance.activeIndex, false), slideChange: (instance) => render(instance.activeIndex) },
      });
    } else {
      render(initialIndex, false);
      if (initialIndex) stage.scrollTo({ left: initialIndex * stage.clientWidth, behavior: 'instant' });
      stage.addEventListener('scroll', () => {
        const target = Math.round(stage.scrollLeft / stage.clientWidth);
        if (target !== index) render(target);
      }, { signal, passive: true });
    }
    const syncMotion = () => {
      const nextReduced = reduced();
      if (nextReduced === lastReduced) return;
      lastReduced = nextReduced;
      if (swiper) {
        swiper.params.speed = swiper.originalParams.speed = nextReduced ? 0 : 360;
        if (nextReduced) select(swiper.activeIndex, { speed: 0 });
      } else if (nextReduced) select(index, { speed: 0 });
    };
    const motionObserver = new MutationObserver(syncMotion);
    motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
    motionQuery.addEventListener('change', syncMotion, { signal });
    return {
      select, selectMedia,
      refresh: () => { swiper?.update(); render(index, false); },
      destroy: () => {
        abort.abort();
        strip?.classList.remove('is-dragging');
        motionObserver.disconnect();
        swiper?.destroy(true, true);
        for (const slide of slides) { slide.inert = false; slide.removeAttribute('aria-hidden'); }
      },
    };
  };
  window.AcProductGallery = { create, ensureSwiper };
})();
